import { platform } from 'constants'
import { IS_WEB } from 'constants/platform'
import { PostTypes } from 'constants/postTypes'
import React, { useEffect, useState } from 'react'
import { StyleSheet } from 'react-native'

import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { Pressable } from 'widgets/Pressable'

import commonStyles from 'utils/styles/commonStyles'

import { Post, StoredPostStep } from 'types/post'

import { PlayerContent } from './widgets/PlayerContent'

const styles = StyleSheet.create({
  pressableStart: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  pressableEnd: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
})

export const PostPlayer = ({
  post,
  sections = [],
  currentSection,
  setCurrentSection,
  setProgress,
  setHeaderVisibility,
}: {
  post: Post
  sections?: StoredPostStep[]
  currentSection: number
  setCurrentSection: (section: number) => void
  setProgress: (progress: number) => void
  setHeaderVisibility: (visible: boolean) => void
}) => {
  const [showCompletePostPage, setShowCompletePostPage] = useState(false)

  const isWorkout = post.type === PostTypes.WORKOUT
  const stepsCount = sections.length
  const isSingleStep = stepsCount === 1
  const isBackEnabled = currentSection >= 1
  const isNextEnabled = currentSection < stepsCount - (!isWorkout ? 1 : 0)
  const isLastStep = stepsCount - 1 === currentSection
  const isFirstStep = currentSection === 0
  const handleOnBack = () => {
    let previousIndex = currentSection - 1
    if (isFirstStep) {
      previousIndex = stepsCount - 1
    }
    setCurrentSection(previousIndex)
  }

  const handleOnNext = () => {
    let nextIndex = currentSection + 1
    if (isLastStep) {
      nextIndex = 0
      setShowCompletePostPage(true)
    }
    setCurrentSection(nextIndex)
  }

  const handleRepeatPost = () => {
    setCurrentSection(0)
    setShowCompletePostPage(false)
  }

  const handleHideCompletePostPage = () => {
    handleOnBack()
    setShowCompletePostPage(false)
  }

  useEffect(() => {
    setHeaderVisibility(!showCompletePostPage)
  }, [setHeaderVisibility, showCompletePostPage])

  return (
    <Box style={commonStyles.flexGrow}>
      <PlayerContent // post content
        post={post}
        steps={sections}
        currentSection={currentSection}
        setProgress={setProgress}
        handleBack={handleHideCompletePostPage}
        handleRepeatPost={handleRepeatPost}
        showCompletePostPage={showCompletePostPage}
      />

      {!showCompletePostPage && ( // post navigation buttons
        <Box
          width='100%'
          height='100%'
          position='absolute'
          flexDirection='row'
          pointerEvents='box-none'
        >
          <Box flex={1} margin='xxs'>
            {(!isSingleStep || isWorkout) && isBackEnabled && (
              <Pressable onPress={handleOnBack} style={styles.pressableStart}>
                {platform.IS_WEB && (
                  <Box
                    testID='PreviousStepButton'
                    width={44}
                    height={44}
                    borderRadius='xl'
                    align='center'
                    justifyContent='center'
                  >
                    <Icon name='chevron-left' color='white' size={32} />
                  </Box>
                )}
              </Pressable>
            )}
          </Box>
          <Box flex={1} margin='xxs' pointerEvents='none' />
          <Box flex={1} margin='xxs' align={IS_WEB ? 'flex-end' : undefined}>
            {(!isSingleStep || isWorkout) && isNextEnabled && (
              <Pressable onPress={handleOnNext} style={styles.pressableEnd}>
                {platform.IS_WEB && (
                  <Box
                    width={44}
                    height={44}
                    borderRadius='xl'
                    align='center'
                    justifyContent='center'
                    testID='NextStepButton'
                  >
                    <Icon name='chevron-right' color='white' size={32} />
                  </Box>
                )}
              </Pressable>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
